/**
 * Copyright 2026 Bluecircle Technologies Pvt Limited. All rights reserved.
 *
 * This software and its source code are the proprietary and confidential
 * intellectual property of Bluecircle Technologies Pvt Limited. Unauthorized
 * copying, reproduction, distribution, modification, public display, or use
 * of this software, in whole or in part, is strictly prohibited without the
 * prior written permission of Bluecircle Technologies Pvt Limited.
 */

/**
 * KNet's shared icon set (@knet/platform-ui, openspec shared-icon-set), drawn the Grist way: a CSS
 * mask filled with the icon colour, so a KNet service looks the same here as in every other
 * service (openspec use-shared-icons).
 */

import KNET_ICON_DATA from "@knet/platform-ui/dist/knet-icons.json";
import { dom, DomElementArg, styled } from "grainjs";

export type KnetIconId = keyof typeof KNET_ICON_DATA;

export interface KnetIconData {
  label: string;
  lucide: string | null;
  tone: string;
  light: string;
  dark: string;
  svg: string;
}

const ICONS: Readonly<Record<string, KnetIconData>> = KNET_ICON_DATA;

/** The entry for an id of the set, or undefined. */
export function knetIconData(id: string): KnetIconData | undefined {
  return Object.prototype.hasOwnProperty.call(ICONS, id) ? ICONS[id] : undefined;
}

/** The CSS mask image of an entry: its SVG as a data URL. */
export function knetIconMask(id: string): string | undefined {
  const data = knetIconData(id);
  return data && `url("data:image/svg+xml;utf8,${encodeURIComponent(data.svg)}")`;
}

/** A 16px icon of the set in the current icon colour; an empty box for an unknown id. */
export function knetIcon(id: KnetIconId, ...domArgs: DomElementArg[]): HTMLElement {
  const data = knetIconData(id);
  return cssKnetIcon(
    data ? [
      dom.style("-webkit-mask-image", knetIconMask(id)!),
      dom.style("mask-image", knetIconMask(id)!),
      dom.attr("title", data.label),
    ] : null,
    ...domArgs,
  );
}

/** A 20px tile in the entry's shared tone, for menus. */
export function knetIconTile(id: KnetIconId, ...domArgs: DomElementArg[]): HTMLElement {
  const data = knetIconData(id);
  return cssKnetTile(
    data ? dom.style("background-color", tint(data.light)) : null,
    knetIcon(id, data ? dom.style("background-color", data.light) : null),
    ...domArgs,
  );
}

/** A 26px squircle navigation tile with 15% wash and 25% boundary border for sidebars. */
export function knetNavIconTile(id: KnetIconId, ...domArgs: DomElementArg[]): HTMLElement {
  const data = knetIconData(id);
  return cssNavKnetTile(
    data ? [
      dom.style("background-color", tint(data.light)),
      dom.style("border-color", borderTint(data.light)),
    ] : null,
    knetIcon(id, data ? dom.style("background-color", data.light) : null),
    ...domArgs,
  );
}

/** A 26px squircle navigation tile for Overview with sky pastel wash and boundary border. */
export function navOverviewTile(...domArgs: DomElementArg[]): HTMLElement {
  const tone = "#0284c7";
  return cssNavKnetTile(
    dom.style("background-color", tint(tone)),
    dom.style("border-color", borderTint(tone)),
    knetIcon("workspace", dom.style("background-color", tone)),
    ...domArgs,
  );
}

/** A 44px squircle badge with 15% wash and 25% boundary border for canvas cards. */
export function knetBadgeIcon(id: KnetIconId, ...domArgs: DomElementArg[]): HTMLElement {
  const data = knetIconData(id);
  return cssBadgeKnetTile(
    data ? [
      dom.style("background-color", tint(data.light)),
      dom.style("border-color", borderTint(data.light)),
    ] : null,
    knetIcon(id, data ? dom.style("background-color", data.light) : null),
    ...domArgs,
  );
}

/** A 42px squircle tile with 15% wash and 25% boundary border for document list item rows. */
export function knetDocTile(id: KnetIconId, ...domArgs: DomElementArg[]): HTMLElement {
  const data = knetIconData(id);
  return cssDocKnetTile(
    data ? [
      dom.style("background-color", tint(data.light)),
      dom.style("border-color", borderTint(data.light)),
    ] : null,
    knetIcon(id, data ? dom.style("background-color", data.light) : null),
    ...domArgs,
  );
}

/** Maps a KNet home item type to its canonical shared icon id. */
export function knetTypeToIconId(type: string): KnetIconId {
  switch (type) {
    case "doc":
      return "databases";
    case "knet-agent":
      return "knets";
    case "bot":
    case "collection":
      return "knowledgebases";
    case "connect-agent":
    case "connect-app":
    case "connector":
    case "connect-account":
      return "connectors";
    case "knet-flow":
      return "flows";
    case "knet-workspace":
      return "workspace";
    case "gis-project":
      return "maps";
    case "crawler":
      return "crawlers";
    default:
      return "workspace";
  }
}

/** Translucent action button style matching the service tone. */
export function toneActionButton(toneHex: string) {
  return [
    dom.style("background-color", tint(toneHex)),
    dom.style("border", `1px solid ${borderTint(toneHex)}`),
    dom.style("color", toneHex),
  ];
}

/** A "#rrggbb" colour at 15% opacity, the shared tiles' tint. */
export function tint(hex: string): string {
  const [r, g, b] = [1, 3, 5].map(i => parseInt(hex.slice(i, i + 2), 16));
  return `rgba(${r}, ${g}, ${b}, 0.15)`;
}

/** A "#rrggbb" colour at 25% opacity, the shared tiles' border tint. */
export function borderTint(hex: string): string {
  const [r, g, b] = [1, 3, 5].map(i => parseInt(hex.slice(i, i + 2), 16));
  return `rgba(${r}, ${g}, ${b}, 0.25)`;
}

const cssKnetIcon = styled("div", `
  position: relative;
  display: inline-block;
  vertical-align: middle;
  flex: none;
  -webkit-mask-repeat: no-repeat;
  -webkit-mask-position: center;
  -webkit-mask-size: contain;
  mask-repeat: no-repeat;
  mask-position: center;
  mask-size: contain;
  width: 16px;
  height: 16px;
  background-color: var(--icon-color, var(--grist-theme-text, black));
`);

const cssKnetTile = styled("div", `
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 6px;
  margin-right: 8px;
  & > div {
    width: 13px;
    height: 13px;
  }
`);

export const cssNavKnetTile = styled("div", `
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 8px;
  border: 1px solid transparent;
  margin-right: 10px;
  transition: all 0.15s cubic-bezier(0.16, 1, 0.3, 1);
  & > div {
    width: 15px;
    height: 15px;
  }
`);

export const cssBadgeKnetTile = styled("div", `
  width: 44px;
  height: 44px;
  border-radius: 13px;
  border: 1px solid transparent;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.35), 0 3px 8px rgba(0, 0, 0, 0.08);
  & > div {
    width: 22px;
    height: 22px;
  }
`);

export const cssDocKnetTile = styled("div", `
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 42px;
  height: 42px;
  border-radius: 12px;
  border: 1px solid transparent;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.35), 0 2px 6px rgba(0, 0, 0, 0.08);
  & > div {
    width: 22px;
    height: 22px;
  }
`);
